import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule, FormControl } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatTreeModule, MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BaseHttpService } from '@app/lib/services/base-http.service';
import { AppConfiguration } from '@app/lib/services/app-config.service';
import { DialogService } from '@app/lib/services/dialog.service';
import { AddMenuDialogComponent } from './add-menu-dialog.component';
import { BreadcrumbComponent } from "@app/lib/components/breadcrumb/breadcrumb.component";
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FlatMenuNode, MenuNode, RoleMenu } from '../models/role-menu-mapping.model';
import { RoleMenuMappingService } from '../services/role-menu-mapping.service';
import { IResponse } from '@app/lib/models/response.model';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';



@Component({
    selector: 'app-role-menu-mapping',
    standalone: true,
    imports: [
        CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule,
        MatTreeModule, MatButtonModule, MatIconModule, MatCheckboxModule,
        MatInputModule, MatSelectModule, MatDialogModule, MatProgressSpinnerModule,
        BreadcrumbComponent, MatFormFieldModule, NgxMatSelectSearchModule
    ],
    templateUrl: './role-menu-mapping.component.html',
    styleUrls: ['./role-menu-mapping.component.scss']
})
export class RoleMenuMappingComponent extends BaseHttpService implements OnInit {
    isSearchComplete: boolean = false;
    onRoleChange(arg0: any) {
        // Obsolete
    }

    searchForm!: FormGroup;
    roles: RoleMenu[] = [];
    isLoading = false;
    showMappingTree = false;
    public newRoleIdctrl: FormControl<string> = new FormControl('', { nonNullable: true });
    public filteredRoles: ReplaySubject<RoleMenu[]> = new ReplaySubject<RoleMenu[]>(1);
    protected _onDestroy = new Subject<void>();

    private _transformer = (node: MenuNode, level: number): FlatMenuNode => {
        return {
            expandable: node.isExpandable !== false,
            level: level,
            original: node,
            isLoading: false
        };
    };

    treeControl = new FlatTreeControl<FlatMenuNode>(
        node => node.level,
        node => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        node => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    loading: boolean = false;
    public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
        { label: 'Admin' },
        { label: 'Role Menu Mapping' },
    ]);

    constructor(
        private fb: FormBuilder,
        protected httpClient: HttpClient,
        appConfig: AppConfiguration,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private service: RoleMenuMappingService
    ) {
        super(httpClient, appConfig);
        this.searchForm = this.fb.group({
            newRoleId: ['', Validators.required],
            roleName: [{ value: '', disabled: true }]
        });
    }

    ngOnInit(): void {
        this.loadRoles();

        this.newRoleIdctrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.filterRoles();
            });
    }
    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }
    hasChild = (_: number, node: FlatMenuNode) => node.expandable;

    private showDialog(title: string, message: string): void {
        this.dialogService.openDialog1({
            data: {
                title: title,
                message: message,
                icon: title.toLowerCase().includes('error') ? 'error' : 'check_small'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });
    }

    loadRoles() {
        this.service.getRoles().subscribe({
            next: (response) => {
                this.roles = response.data;
                this.filteredRoles.next(this.roles.slice());
            },
            error: (error) => {
                console.error('Error fetching ID Types:', error);
            }
        });
    }
    protected filterRoles() {
        if (!this.roles) return;

        let search = this.newRoleIdctrl.value;
        if (!search) {
            this.filteredRoles.next(this.roles.slice());
            return;
        } else {
            search = search.toLowerCase();
        }

        // Filter based on role name or id
        this.filteredRoles.next(
            this.roles.filter(role =>
                role.name.toLowerCase().includes(search) ||
                (role.id && role.id.toString().toLowerCase().includes(search))
            )
        );
    }

    onRoleSelect(roleId: any): void {
        const role = this.roles.find(r => r.id === roleId);
        if (role) {
            this.searchForm.patchValue({ roleName: role.name });
        } else {
            this.searchForm.patchValue({ roleName: '' });
        }
    }

    onSearch(): void {
        if (this.searchForm.invalid) return;
        this.isLoading = true;
        const reqParams = {
            'newRoleId': this.searchForm.value.newRoleId,
            'node': 'root'
        }
        this.service.getData(reqParams).subscribe({
            next: (response) => {
                if (response && response.data) {
                    this.dataSource.data = response.data.map((item: any) => {
                        const hasViewAccess = item.view === true || item.view === 'Y' || item.view === 'true';
                        return {
                            ...item,
                            isExpandable: true,
                            viewAccess: hasViewAccess,
                            originalView: hasViewAccess, // Track original state
                            hasChildrenLoaded: false
                        };
                    });
                } else {
                    this.dataSource.data = [];
                }
                this.showMappingTree = true;
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error fetching ID Types:', error);
            }
        });

    }

    handleReset() {
        this.onReset();
    }
    toggleNode(node: FlatMenuNode): void {
        if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
        } else {
            if (!node.original.hasChildrenLoaded) {
                node.isLoading = true;

                const reqParams = {
                    'newRoleId': this.searchForm.value.newRoleId,
                    'node': (node.original as any).id.toString()
                };

                this.service.getData(reqParams).subscribe({
                    next: (response: any) => {
                        let children = [];
                        if (response && response.data) {
                            children = response.data.map((item: any) => ({
                                ...item,
                                isExpandable: true,
                                viewAccess: item.view === true || item.view === 'Y' || item.view === 'true',
                                originalView: item.view === true || item.view === 'Y' || item.view === 'true', // Track original state
                                hasChildrenLoaded: false
                            }));
                        }

                        node.original.hasChildrenLoaded = true;
                        node.original.children = children;
                        node.original.isExpandable = children.length > 0;
                        node.isLoading = false;

                        this.refreshTree();

                        if (children.length > 0) {
                            const newNode = this.treeControl.dataNodes.find(n => (n.original as any).id === (node.original as any).id);
                            if (newNode) {
                                this.treeControl.expand(newNode);
                            }
                        }
                    },
                    error: (err: any) => {
                        console.error('Error fetching submenus:', err);
                        node.isLoading = false;
                    }
                });
            } else {
                this.treeControl.expand(node);
            }
        }
    }

    refreshTree(): void {
        const expandedState = this.treeControl.expansionModel.selected.map(n => (n.original as any).id);

        const data = this.dataSource.data;
        this.dataSource.data = [];
        this.dataSource.data = data;

        this.treeControl.dataNodes.forEach(n => {
            if (expandedState.includes((n.original as any).id)) {
                this.treeControl.expand(n);
            }
        });
    }


    onReset(): void {
        this.searchForm.reset();
        this.showMappingTree = false;
        this.dataSource.data = [];
    }

    onParentCheckboxChange(node: FlatMenuNode, checked: boolean): void {
        if (node.original.children) {
            this.setChildrenAccess(node.original.children, checked);

            // Re-render tree to show child updates
            this.refreshTree();
        }
    }

    private setChildrenAccess(children: MenuNode[], checked: boolean): void {
        children.forEach(c => {
            c.viewAccess = checked;
            if (c.children) this.setChildrenAccess(c.children, checked);
        });
    }

    openAddDialog(): void {
        this.isLoading = true;
        const reqParams = {
            'id': this.searchForm.value.newRoleId,
        };
        const params = {
            'newRoleId': this.searchForm.value.newRoleId,
            'menuId': ''
        };
        this.service.getParentMenu(reqParams).subscribe({
            next: (response) => {
                if (response && response.data) {
                    const dialogRef = this.dialog.open(AddMenuDialogComponent, {
                        width: '600px',
                        data: { menus: response.data },
                        disableClose: true
                    });

                    dialogRef.afterClosed().subscribe(result => {
                        if (result && result.length > 0) {
                            const newData: any = [];
                            result.forEach((m: MenuNode) => {
                                newData.push({
                                    id: m.id,
                                    menuName: m.menuName,
                                    access: true
                                });
                            });


                            this.service.insertMenu(newData, params).subscribe({
                                next: (response) => {
                                    response.success ? this.showDialog('Success', 'Role Menu saved successfully.') :
                                        this.showDialog('Failure', 'Role Menu failed.');
                                    this.onSearch();
                                },
                                error: (error) => {
                                    console.error('Error fetching ID Types:', error);
                                }
                            });


                        }
                    });
                } else {
                    this.onSearch();
                }
                this.showMappingTree = true;
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error fetching ID Types:', error);
            }
        });

    }

    private flattenMenuTree(nodes: any[]): any[] {
        let payload: any[] = [];
        nodes.forEach(node => {
            // Clone to avoid mutating the UI tree model directly
            const item = { ...node };
            // Map the UI checkbox state back to the 'view' property
            item.view = !!item.viewAccess;

            // IF CHANGED, add to payload
            const isChanged = (item.view !== item.originalView);

            // Clean up temporary UI flags
            delete item.isExpandable;
            delete item.viewAccess;
            delete item.hasChildrenLoaded;
            delete item.originalView;
            // The sample provided expects 'children' to be null instead of an array
            const children = item.children;
            item.children = null;

            if (isChanged) {
                payload.push(item);
            }

            if (children && children.length > 0) {
                payload = payload.concat(this.flattenMenuTree(children));
            }
        });
        return payload;
    }

    hasChanges(): boolean {
        return this.checkNodeChanges(this.dataSource.data);
    }

    private checkNodeChanges(nodes: any[]): boolean {
        if (!nodes) return false;
        for (const node of nodes) {
            if (node.originalView !== !!node.viewAccess) return true;
            if (node.children && this.checkNodeChanges(node.children)) return true;
        }
        return false;
    }

    confirmSave(): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to save these mapping changes?",
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.onSave();
            }
        });
    }

    onSave(): void {
        this.isLoading = true;

        const generatedMenus = this.flattenMenuTree(this.dataSource.data);
       

        const payload = {
            newRoleId: this.searchForm.value.newRoleId,

        };

        this.service.upsertMenu(generatedMenus, payload).subscribe({
            next: (response) => {
                response.success ? this.showDialog('Success', 'Role Menu saved successfully.') :
                    this.showDialog('Failure', 'Role Menu failed.');
                this.onSearch();
            },
            error: (error) => {
                console.error('Error fetching ID Types:', error);
            }
        });
    }
}
